import { useEffect, useMemo, useState } from 'react'

import { api, ApiError } from '../api/client'
import type { ApprovalRecord } from '../store/useObservabilityStore'
import { useWriteAccess } from '../lib/writeAccess'

type ApprovalReviewPanelProps = {
  approvals: Record<string, ApprovalRecord>
  onDecision: () => Promise<void>
}

type DecisionState = 'idle' | 'submitting' | 'error'

function prettyJSON(value: unknown) {
  return JSON.stringify(value ?? {}, null, 2)
}

export function ApprovalReviewPanel({ approvals, onDecision }: ApprovalReviewPanelProps) {
  const pendingApprovals = useMemo(
    () =>
      Object.values(approvals)
        .filter((item) => item.status === 'pending')
        .sort((a, b) => a.requestedAt.localeCompare(b.requestedAt)),
    [approvals],
  )
  const [selectedId, setSelectedId] = useState<string>()
  const [editorValue, setEditorValue] = useState('{}')
  const [decisionState, setDecisionState] = useState<DecisionState>('idle')
  const [error, setError] = useState<string | null>(null)
  // Approvals decide is records-level (owner or admin — lib/writeAccess).
  const { records } = useWriteAccess()

  useEffect(() => {
    if (pendingApprovals.length === 0) {
      setSelectedId(undefined)
      setEditorValue('{}')
      return
    }
    const selected = pendingApprovals.find((item) => item.id === selectedId) || pendingApprovals[0]
    setSelectedId(selected.id)
    setEditorValue(prettyJSON(selected.payload))
  }, [pendingApprovals, selectedId])

  const selectedApproval = pendingApprovals.find((item) => item.id === selectedId)

  async function submitDecision(status: 'approved' | 'rejected') {
    if (!selectedApproval) {
      return
    }

    setDecisionState('submitting')
    setError(null)

    let editedPayload: Record<string, unknown> | undefined
    if (status === 'approved') {
      try {
        editedPayload = JSON.parse(editorValue) as Record<string, unknown>
      } catch (err) {
        setDecisionState('error')
        setError(err instanceof Error ? err.message : 'Edited payload is not valid JSON')
        return
      }
    }

    try {
      // Routes through the shared api client: fresh bearer token + 401 handling,
      // and X-Tenant-Id from the active tenant (ignored by the backend in workos
      // mode).
      await api.post(`/v1/approvals/${encodeURIComponent(selectedApproval.id)}/decision`, {
        status,
        ...(status === 'approved' ? { editedPayload: editedPayload } : {}),
      })
    } catch (err) {
      setDecisionState('error')
      setError(
        err instanceof ApiError
          ? err.body || `Failed to ${status} approval`
          : err instanceof Error
            ? err.message
            : `Failed to ${status} approval`,
      )
      return
    }

    await onDecision()
    setDecisionState('idle')
  }

  return (
    <section
      style={{
        background: 'linear-gradient(180deg, rgba(28,20,8,0.96) 0%, rgba(16,12,6,0.98) 100%)',
        border: '1px solid rgba(247, 185, 85, 0.22)',
        borderRadius: 16,
        padding: 20,
        color: '#f7f0dd',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#d8b672',
              marginBottom: 6,
            }}
          >
            Review Station
          </div>
          <div style={{ fontSize: 14, color: '#f0dfb6' }}>
            {pendingApprovals.length > 0 ? `${pendingApprovals.length} approval${pendingApprovals.length === 1 ? '' : 's'} waiting` : 'No approvals waiting'}
          </div>
        </div>
      </div>

      {pendingApprovals.length === 0 ? (
        <div style={{ color: '#cfbf95' }}>This run does not currently need human approval.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: 16 }}>
          <div
            style={{
              border: '1px solid rgba(247, 185, 85, 0.12)',
              borderRadius: 12,
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            {pendingApprovals.map((approval) => {
              const isActive = approval.id === selectedApproval?.id
              return (
                <button
                  key={approval.id}
                  type="button"
                  onClick={() => setSelectedId(approval.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    borderBottom: '1px solid rgba(247, 185, 85, 0.08)',
                    background: isActive ? 'rgba(247, 185, 85, 0.1)' : 'transparent',
                    color: '#f7f0dd',
                    padding: '14px 16px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{approval.skillId || approval.stepId || 'approval'}</div>
                  <div style={{ fontSize: 12, color: '#d7c79e' }}>{new Date(approval.requestedAt).toLocaleString()}</div>
                </button>
              )
            })}
          </div>

          <div
            style={{
              border: '1px solid rgba(247, 185, 85, 0.12)',
              borderRadius: 12,
              background: 'rgba(255,255,255,0.02)',
              padding: 16,
            }}
          >
            {selectedApproval ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
                  <ApprovalMeta label="Approval ID" value={selectedApproval.id} mono />
                  <ApprovalMeta label="Run ID" value={selectedApproval.runId} mono />
                  <ApprovalMeta label="Step" value={selectedApproval.stepId || 'run-level'} />
                  <ApprovalMeta label="Skill" value={selectedApproval.skillId || 'n/a'} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
                  <div>
                    <div style={sectionLabelStyle}>Proposed Report</div>
                    <pre style={preStyle}>{String(selectedApproval.payload?.report || '—')}</pre>
                  </div>
                  <div>
                    <div style={sectionLabelStyle}>Editable Payload JSON</div>
                    <textarea
                      value={editorValue}
                      onChange={(event) => setEditorValue(event.target.value)}
                      spellCheck={false}
                      style={{
                        width: '100%',
                        minHeight: 220,
                        resize: 'vertical',
                        borderRadius: 12,
                        border: '1px solid rgba(247, 185, 85, 0.14)',
                        background: '#0f1115',
                        color: '#f8f3e3',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 12,
                        padding: 12,
                      }}
                    />
                  </div>
                </div>

                {error ? (
                  <div style={{ marginTop: 14, color: '#ffb4a9', whiteSpace: 'pre-wrap' }}>{error}</div>
                ) : null}

                {records && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={() => void submitDecision('rejected')}
                    disabled={decisionState === 'submitting'}
                    style={secondaryButtonStyle}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitDecision('approved')}
                    disabled={decisionState === 'submitting'}
                    style={primaryButtonStyle}
                  >
                    {decisionState === 'submitting' ? 'Submitting…' : 'Approve & Commit'}
                  </button>
                </div>}
              </>
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}

function ApprovalMeta({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        border: '1px solid rgba(247, 185, 85, 0.1)',
        borderRadius: 12,
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <div style={sectionLabelStyle}>{label}</div>
      <div
        style={{
          color: '#fff8e5',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' : undefined,
          fontSize: 13,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </div>
    </div>
  )
}

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: '#d8b672',
  marginBottom: 8,
}

const preStyle = {
  margin: 0,
  minHeight: 220,
  borderRadius: 12,
  border: '1px solid rgba(247, 185, 85, 0.14)',
  background: '#0f1115',
  color: '#f8f3e3',
  fontSize: 12,
  lineHeight: 1.6,
  padding: 12,
  whiteSpace: 'pre-wrap' as const,
  overflowWrap: 'anywhere' as const,
}

const secondaryButtonStyle = {
  border: '1px solid rgba(247, 185, 85, 0.18)',
  background: 'transparent',
  color: '#f7e0b0',
  borderRadius: 10,
  padding: '10px 14px',
  fontWeight: 700,
  cursor: 'pointer',
}

const primaryButtonStyle = {
  border: '1px solid rgba(247, 185, 85, 0.34)',
  background: '#f7b955',
  color: '#1f1402',
  borderRadius: 10,
  padding: '10px 14px',
  fontWeight: 800,
  cursor: 'pointer',
}
